// @ts-check
'use strict';

/**
 * The phrase library: pre-filled, editable condition commentary built from the
 * defects an assessor taps. Deterministic and offline — no AI. Ported from
 * Site2Scope's src/conditionLibrary.js; the wording was mined from real Higgins
 * condition reports and is kept as written there.
 */

/** @typedef {import('./scales').Band} Band */
/** @typedef {'lined' | 'timber' | 'sheet' | 'metal' | 'masonry' | 'generic'} Family */
/** @typedef {'internal' | 'external'} Side */

/**
 * Substrate → material family, which decides the defects offered. Unmapped and
 * custom surfaces fall back to 'generic' (paint-film defects only).
 *
 * @type {Readonly<Record<string, Family>>}
 */
const SUBSTRATE_FAMILY = Object.freeze({
  // Painted plasterboard / lined internal
  'Ceilings': 'lined',
  'Walls': 'lined', // internal; external walls resolve to masonry in familyForSubstrate
  'Skirting': 'timber',
  // Timber joinery
  'Doors': 'timber',
  'Windows': 'timber',
  'Stairs': 'timber',
  'Fascia': 'timber', // many AU fascias are timber; metal is the assessor's defect choice
  'Posts': 'timber',
  'Balustrade / Handrails': 'timber',
  'Timber Play Equipment': 'timber',
  // Eaves / soffit (usually fibre-cement or lined sheet)
  'Eaves': 'sheet',
  'Soffit': 'sheet',
  // Metal
  'Gutter': 'metal',
  'Downpipes': 'metal',
  'Roofs': 'metal',
  // Masonry / render / concrete
  'Floors': 'masonry',
  'Slab Edge': 'masonry',
});

/**
 * @param {string} substrate
 * @param {string | null} [baseSurface]  the surface a custom block is based on
 * @param {Side | null} [side]
 * @returns {Family}
 */
function familyForSubstrate(substrate, baseSurface, side) {
  const key = baseSurface || substrate;
  // External walls are commonly render/masonry; internal are lined plasterboard.
  if (key === 'Walls') return side === 'external' ? 'masonry' : 'lined';
  return SUBSTRATE_FAMILY[key] || 'generic';
}

/**
 * @typedef {object} Defect
 * @property {string} id
 * @property {string} label       chip text
 * @property {string} phrase      lower-case clause that follows "[Substrate]: "
 * @property {Family[]} families  'generic' = offered for every family
 * @property {boolean} makeGood   implies pre-paint repair
 */

/** @type {ReadonlyArray<Defect>} */
const DEFECTS = Object.freeze([
  // Universal paint-film defects (all families)
  { id: 'peeling', label: 'Peeling / flaking', phrase: 'paint is peeling and flaking', families: ['generic'], makeGood: false },
  { id: 'blistering', label: 'Blistering / bubbling', phrase: 'the coating is blistering and bubbling', families: ['generic'], makeGood: false },
  { id: 'cracking_film', label: 'Cracking (paint)', phrase: 'the paint film is cracking', families: ['generic'], makeGood: false },
  { id: 'alligatoring', label: 'Alligatoring', phrase: 'the paint shows alligatoring / crocodiling', families: ['generic'], makeGood: false },
  { id: 'chalking', label: 'Chalking', phrase: 'the surface is chalking', families: ['generic'], makeGood: false },
  { id: 'fading', label: 'Fading / colour loss', phrase: 'the colour has faded', families: ['generic'], makeGood: false },
  { id: 'discolouration', label: 'Discolouration', phrase: 'the paint is discoloured', families: ['generic'], makeGood: false },
  { id: 'loss_gloss', label: 'Loss of gloss', phrase: 'the finish has lost its gloss', families: ['generic'], makeGood: false },
  { id: 'dirt_grime', label: 'Dirt / grime', phrase: 'the surface is heavily soiled with dirt and grime', families: ['generic'], makeGood: false },
  { id: 'poor_coverage', label: 'Poor coverage', phrase: 'the previous coating has not covered adequately', families: ['generic'], makeGood: false },
  { id: 'bleed_through', label: 'Bleed-through', phrase: 'the original colour is bleeding through', families: ['generic'], makeGood: false },
  { id: 'holes_patching', label: 'Holes / patching', phrase: 'there are holes and patched areas requiring make good', families: ['generic'], makeGood: true },
  { id: 'mechanical_dmg', label: 'Wear & tear / damage', phrase: 'there are visible marks of wear and mechanical damage', families: ['generic'], makeGood: false },
  { id: 'poor_prep', label: 'Poor prior prep', phrase: 'the previous coating was applied without adequate preparation', families: ['generic'], makeGood: false },
  { id: 'delamination', label: 'Delamination', phrase: 'the coating is delaminating and lifting from the substrate', families: ['generic'], makeGood: false },
  { id: 'tacky', label: 'Tacky / uncured', phrase: 'the coating remains tacky and has not cured', families: ['generic'], makeGood: false },
  { id: 'wrong_colour', label: 'Wrong colour scheme', phrase: 'the colour scheme does not match the current specification', families: ['generic'], makeGood: false },
  { id: 'never_painted', label: 'Never painted / bare', phrase: 'the substrate appears to have never been painted', families: ['generic'], makeGood: false },
  { id: 'paint_failure', label: 'General paint failure', phrase: 'the paint has failed', families: ['generic'], makeGood: false },
  { id: 'lap_marks', label: 'Lap marks / uneven', phrase: 'the finish is uneven with visible lap marks', families: ['generic'], makeGood: false },

  // Biological (lined wet areas + all exterior families)
  { id: 'mould', label: 'Mould / mildew', phrase: 'mould and mildew are present', families: ['lined', 'sheet', 'timber', 'masonry', 'metal'], makeGood: true },
  { id: 'algae', label: 'Algae / green staining', phrase: 'algae and green staining are present', families: ['sheet', 'timber', 'masonry', 'metal'], makeGood: true },
  { id: 'lichen', label: 'Lichen growth', phrase: 'lichen growth is established on the surface', families: ['sheet', 'masonry', 'metal'], makeGood: true },

  // Timber
  { id: 'rot', label: 'Timber rot / decay', phrase: 'the timber is showing signs of rot and decay', families: ['timber'], makeGood: true },
  { id: 'soft_grain', label: 'Soft / spongy timber', phrase: 'sections of timber have gone soft and require repair', families: ['timber'], makeGood: true },
  { id: 'splitting', label: 'Splitting / cracking', phrase: 'the timber is splitting and cracking', families: ['timber'], makeGood: true },
  { id: 'raised_grain', label: 'Raised / weathered grain', phrase: 'the grain has raised and weathered', families: ['timber'], makeGood: false },
  { id: 'knot_bleed', label: 'Knot / resin bleed', phrase: 'knot and resin bleed is showing through the coating', families: ['timber'], makeGood: false },
  { id: 'end_grain', label: 'End-grain moisture', phrase: 'moisture damage is evident at the end grain', families: ['timber'], makeGood: true },
  { id: 'borer', label: 'Insect / borer damage', phrase: 'there is evidence of insect / borer damage', families: ['timber'], makeGood: true },
  { id: 'warping', label: 'Warping / cupping', phrase: 'the timber has warped and cupped', families: ['timber'], makeGood: true },

  // Metal
  { id: 'surface_rust', label: 'Surface rust', phrase: 'surface rust and corrosion are present', families: ['metal'], makeGood: true },
  { id: 'rust_perforation', label: 'Rust perforation', phrase: 'corrosion has perforated the metal', families: ['metal'], makeGood: true },
  { id: 'oxidation', label: 'Oxidation / chalked finish', phrase: 'the factory finish has oxidised and chalked', families: ['metal'], makeGood: false },
  { id: 'salt_corrosion', label: 'Salt / coastal corrosion', phrase: 'salt deposits and coastal corrosion are evident', families: ['metal'], makeGood: true },
  { id: 'fastener_rust', label: 'Fastener corrosion', phrase: 'corroded fasteners are staining the surface', families: ['metal'], makeGood: true },

  // Masonry / render / concrete
  { id: 'hairline_crack', label: 'Hairline / map cracking', phrase: 'hairline and map cracking are present', families: ['masonry'], makeGood: true },
  { id: 'structural_crack', label: 'Structural cracking', phrase: 'structural cracking is evident and should be assessed', families: ['masonry'], makeGood: true },
  { id: 'efflorescence', label: 'Efflorescence (salt)', phrase: 'efflorescence (salt bloom) is present', families: ['masonry'], makeGood: true },
  { id: 'spalling', label: 'Spalling / concrete cancer', phrase: 'the surface is spalling and breaking away', families: ['masonry'], makeGood: true },
  { id: 'drummy_render', label: 'Drummy / hollow render', phrase: 'sections of render are drummy and delaminating', families: ['masonry'], makeGood: true },
  { id: 'rising_damp', label: 'Rising damp / moisture', phrase: 'moisture staining consistent with rising damp is visible', families: ['masonry'], makeGood: true },

  // Sheet (fibre-cement eaves / soffit)
  { id: 'sheet_cracking', label: 'Sheet cracking', phrase: 'the sheeting is cracking', families: ['sheet'], makeGood: true },
  { id: 'sheet_joint', label: 'Failed joints / stopping', phrase: 'the sheet joints and stopping have failed', families: ['sheet'], makeGood: true },
  { id: 'sagging_sheet', label: 'Sagging / water damage', phrase: 'the sheeting is sagging from water damage', families: ['sheet'], makeGood: true },
]);

/** @type {Readonly<Record<string, Defect>>} */
const DEFECT_BY_ID = Object.freeze(Object.fromEntries(DEFECTS.map((d) => [d.id, d])));

/**
 * The defect chips offered for a family: family-specific first, then universal.
 *
 * @param {Family} family
 * @returns {Defect[]}
 */
function defectsForFamily(family) {
  const specific = DEFECTS.filter((d) => d.families.includes(family) && !d.families.includes('generic'));
  const universal = DEFECTS.filter((d) => d.families.includes('generic'));
  return [...specific, ...universal];
}

/** How widespread the defects are. */
const EXTENT_OPTIONS = Object.freeze([
  { key: 'isolated', label: 'Isolated areas', phrase: 'in isolated areas' },
  { key: 'some', label: 'Some areas', phrase: 'in some areas' },
  { key: 'multiple', label: 'Multiple areas', phrase: 'in multiple areas' },
  { key: 'widespread', label: 'Widespread', phrase: 'in a large number of areas' },
  { key: 'throughout', label: 'Throughout', phrase: 'throughout' },
]);

/** @type {Readonly<Record<Band, string>>} */
const BAND_CLOSER = Object.freeze({
  poor: 'Recommended for repaint in line with the coating specification.',
  fair: 'Recommended for repaint, however a reduced scope may be considered.',
  good: 'Sound paint film in good condition — no work recommended.',
});

const MAKE_GOOD_CLOSER = 'Pre-paint repair and make good is required prior to the application of the new coating system.';

/**
 * @typedef {object} NoteInput
 * @property {Band | null} [band]
 * @property {string[]} [defects]        defect ids
 * @property {string[]} [customDefects]  the assessor's own clauses
 * @property {string | null} [extent]    an EXTENT_OPTIONS key
 */

/**
 * The pre-filled note for one item: "[Substrate]: [defects] [extent]." then a
 * make-good line when any tapped defect needs repair, then a closer set by the
 * band. Pure. The app stores what the assessor edits and never regenerates over it.
 *
 * @param {string} substrate
 * @param {NoteInput | null | undefined} entry
 * @returns {string}
 */
function generateConditionNote(substrate, entry) {
  if (!entry) return '';
  const band = entry.band || null;
  const defectIds = Array.isArray(entry.defects) ? entry.defects : [];
  const customPhrases = (Array.isArray(entry.customDefects) ? entry.customDefects : [])
    .filter((s) => s && s.trim())
    .map((s) => s.trim());

  if (band === 'good' && defectIds.length === 0 && customPhrases.length === 0) {
    return `${substrate} — ${BAND_CLOSER.good}`;
  }
  if (defectIds.length === 0 && customPhrases.length === 0) {
    return band ? BAND_CLOSER[band] : '';
  }

  const defs = defectIds.map((id) => DEFECT_BY_ID[id]).filter(Boolean);
  const extent = EXTENT_OPTIONS.find((e) => e.key === entry.extent) || null;
  const extentPhrase = extent ? ` ${extent.phrase}` : '';
  const phrases = [...defs.map((d) => d.phrase), ...customPhrases];

  let body;
  if (phrases.length === 1) {
    body = `${substrate}: ${phrases[0]}${extentPhrase}.`;
  } else {
    const last = phrases[phrases.length - 1];
    body = `${substrate}: ${phrases.slice(0, -1).join(', ')} and ${last}${extentPhrase}.`;
  }
  body = body.replace(/: ([a-z])/, (_m, c) => `: ${c.toUpperCase()}`);

  const closers = [];
  if (defs.some((d) => d.makeGood)) closers.push(MAKE_GOOD_CLOSER);
  if (band) closers.push(BAND_CLOSER[band]);
  return [body, ...closers].join(' ').trim();
}

module.exports = {
  SUBSTRATE_FAMILY,
  familyForSubstrate,
  DEFECTS,
  DEFECT_BY_ID,
  defectsForFamily,
  EXTENT_OPTIONS,
  generateConditionNote,
};
